import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Link } from '@mui/material';
import {
  MapMarker,
  MapRoutePoint,
  INDIA_CENTER,
  INDIA_ZOOM,
  hasCoordinates,
  statusColor,
  getGoogleMapsApiKey,
} from './mapUtils';
import { loadGoogleMaps } from './googleMapsLoader';

interface VehicleMapProps {
  markers?: MapMarker[];
  route?: MapRoutePoint[];
  height?: number | string;
  center?: [number, number];
  zoom?: number;
  onMarkerClick?: (id: string) => void;
  fitOnDataLoad?: boolean;
  fitKey?: string;
  emptyMessage?: string;
}

export const VehicleMap: React.FC<VehicleMapProps> = ({
  markers = [],
  route = [],
  height = 420,
  center = INDIA_CENTER,
  zoom = INDIA_ZOOM,
  onMarkerClick,
  fitOnDataLoad = true,
  fitKey = 'default',
  emptyMessage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<Array<google.maps.Marker | google.maps.Polyline>>([]);
  const onMarkerClickRef = useRef(onMarkerClick);
  const hasFittedRef = useRef(false);
  const fitKeyRef = useRef(fitKey);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const apiKey = getGoogleMapsApiKey();
  onMarkerClickRef.current = onMarkerClick;

  useEffect(() => {
    if (fitKey !== fitKeyRef.current) {
      fitKeyRef.current = fitKey;
      hasFittedRef.current = false;
    }
  }, [fitKey]);

  useEffect(() => {
    if (!apiKey) {
      setMapError('missing-key');
      return undefined;
    }

    const container = containerRef.current;
    if (!container) return undefined;
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then((gmaps) => {
        if (cancelled || !containerRef.current) return;
        mapRef.current = new gmaps.Map(containerRef.current, {
          center: { lat: center[0], lng: center[1] },
          zoom,
          mapTypeId: gmaps.MapTypeId.ROADMAP,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });
        setMapReady(true);
        setMapError(null);
      })
      .catch(() => {
        if (!cancelled) setMapError('load-failed');
      });

    return () => {
      cancelled = true;
      overlaysRef.current.forEach((layer) => layer.setMap(null));
      overlaysRef.current = [];
      mapRef.current = null;
      setMapReady(false);
    };
    // Map is created once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !window.google?.maps) return;

    overlaysRef.current.forEach((layer) => layer.setMap(null));
    overlaysRef.current = [];

    const gmaps = window.google.maps;
    const bounds = new gmaps.LatLngBounds();
    const shouldFit = fitOnDataLoad && !hasFittedRef.current;

    if (route.length > 1) {
      const path = route.filter((p) => hasCoordinates(p.lat, p.lng)).map((p) => ({ lat: p.lat, lng: p.lng }));
      if (path.length > 1) {
        const line = new gmaps.Polyline({
          path,
          geodesic: true,
          strokeColor: '#6221d6',
          strokeOpacity: 0.85,
          strokeWeight: 4,
          map,
        });
        overlaysRef.current.push(line);
        path.forEach((p) => bounds.extend(p));
      }
    }

    markers.filter((m) => hasCoordinates(m.lat, m.lng)).forEach((m) => {
      const color = m.selected ? '#6221d6' : statusColor(m.status);
      const marker = new gmaps.Marker({
        position: { lat: m.lat, lng: m.lng },
        map,
        title: m.label || m.id,
        icon: {
          path: gmaps.SymbolPath.CIRCLE,
          scale: m.selected ? 10 : 8,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      if (m.label) {
        const info = new gmaps.InfoWindow({ content: m.label });
        marker.addListener('click', () => {
          info.open({ map, anchor: marker });
          onMarkerClickRef.current?.(m.id);
        });
      } else {
        marker.addListener('click', () => onMarkerClickRef.current?.(m.id));
      }
      overlaysRef.current.push(marker);
      bounds.extend({ lat: m.lat, lng: m.lng });
    });

    const hasPoints = !bounds.isEmpty();
    if (shouldFit && hasPoints) {
      if (markers.length === 1 && route.length < 2) {
        map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        map.setZoom(13);
      } else {
        map.fitBounds(bounds, 48);
      }
      hasFittedRef.current = true;
    }
  }, [markers, route, fitOnDataLoad, mapReady]);

  const overlayMessage = !apiKey
    ? null
    : emptyMessage && markers.length === 0 && route.length < 2
      ? emptyMessage
      : mapError === 'load-failed'
        ? 'Google Maps failed to load. Check the API key and that Maps JavaScript API is enabled.'
        : null;

  return (
    <Box
      sx={{
        height,
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        bgcolor: 'grey.100',
      }}
    >
      {!apiKey && (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, textAlign: 'center' }}>
            Google Maps is required for live tracking. Add{' '}
            <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> to <code>client/.env</code>, enable
            Maps JavaScript API in Google Cloud, then restart the client.
            See{' '}
            <Link href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noreferrer">
              Google Maps Platform
            </Link>
            .
          </Typography>
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{ height: '100%', width: '100%', display: apiKey ? 'block' : 'none' }}
      />
      {overlayMessage && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 1,
            p: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              bgcolor: 'background.paper',
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              maxWidth: 420,
              textAlign: 'center',
            }}
          >
            {overlayMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
